import {
  IssueSourceDtoEntityTypeEnum,
  UserSortingDtoOptions,
} from '@issuemanager/api-client';
import { ExtendedAxiosError } from '@issuemanager/apps/client/hooks';
import { SourceEntityTypeEnum } from '@issuemanager/constants';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

interface ObjectFilterDtoOptions {
  searchTerm: string;
  type: IssueSourceDtoEntityTypeEnum;
}

export type UseInfiniteObjectParams = {
  sort?: UserSortingDtoOptions;
  filter?: ObjectFilterDtoOptions;
  page?: number;
  limit?: number;
  enabled?: boolean;
  handleError?: boolean;
};

interface MockObject {
  id: string;
  label: string;
}

interface PaginatedMockObject {
  data: MockObject[];
  meta: {
    page: number;
    limit: number;
    itemCount: number;
    pageCount: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
}

const mockData = [
  {
    id: '1',
    label: 'R001 - Audit 1',
    type: SourceEntityTypeEnum.AUDIT,
  },
  {
    id: '2',
    label: 'R002 - Control 2',
    type: SourceEntityTypeEnum.CONTROL,
  },
  {
    id: '3',
    label: 'R003 - Control Assessment 3',
    type: SourceEntityTypeEnum.CONTROL_ASSESSMENT,
  },
  {
    id: '4',
    label: 'R004 - Objective 4',
    type: SourceEntityTypeEnum.OBJECTIVE,
  },
  {
    id: '5',
    label: 'R005 - Process 5',
    type: SourceEntityTypeEnum.PROCESS,
  },
  {
    id: '6',
    label: 'R006 - Risk 6',
    type: SourceEntityTypeEnum.RISK,
  },
  {
    id: '7',
    label: 'R007 - Risk Assessment 7',
    type: SourceEntityTypeEnum.RISK_ASSESSMENT,
  },
  {
    id: '8',
    label: 'R008 - Risk Mitigation 8',
    type: SourceEntityTypeEnum.RISK_MITIGATION,
  },
  {
    id: '11',
    label: 'R0011 - Audit 1',
    type: SourceEntityTypeEnum.AUDIT,
  },
  {
    id: '12',
    label: 'R0012 - Control 2',
    type: SourceEntityTypeEnum.CONTROL,
  },
  {
    id: '13',
    label: 'R0013 - Control Assessment 3',
    type: SourceEntityTypeEnum.CONTROL_ASSESSMENT,
  },
  {
    id: '14',
    label: 'R0014 - Objective 4',
    type: SourceEntityTypeEnum.OBJECTIVE,
  },
  {
    id: '15',
    label: 'R0015 - Process 5',
    type: SourceEntityTypeEnum.PROCESS,
  },
  {
    id: '17',
    label: 'R0017 - Risk Assessment 7',
    type: SourceEntityTypeEnum.RISK_ASSESSMENT,
  },
  {
    id: '18',
    label: 'R0018 - Risk Mitigation 8',
    type: SourceEntityTypeEnum.RISK_MITIGATION,
  },
  {
    id: '21',
    label: 'R0021 - Audit 1',
    type: SourceEntityTypeEnum.AUDIT,
  },
  {
    id: '22',
    label: 'R0022 - Control 2',
    type: SourceEntityTypeEnum.CONTROL,
  },
  {
    id: '23',
    label: 'R0023 - Control Assessment 3',
    type: SourceEntityTypeEnum.CONTROL_ASSESSMENT,
  },
  {
    id: '24',
    label: 'R0024 - Objective 4',
    type: SourceEntityTypeEnum.OBJECTIVE,
  },
  {
    id: '25',
    label: 'R0025 - Process 5',
    type: SourceEntityTypeEnum.PROCESS,
  },
  {
    id: '27',
    label: 'R0027 - Risk Assessment 7',
    type: SourceEntityTypeEnum.RISK_ASSESSMENT,
  },
  {
    id: '28',
    label: 'R0028 - Risk Mitigation 8',
    type: SourceEntityTypeEnum.RISK_MITIGATION,
  },
  {
    id: '26',
    label: 'R0026 - Risk 6',
    type: SourceEntityTypeEnum.RISK,
  },
  {
    id: '16',
    label: 'R0016 - Risk 6',
    type: SourceEntityTypeEnum.RISK,
  },
  {
    id: '36',
    label: 'R00326 - Risk 6',
    type: SourceEntityTypeEnum.RISK,
  },
  {
    id: '46',
    label: 'R00416 - Risk 6',
    type: SourceEntityTypeEnum.RISK,
  },
  {
    id: '56',
    label: 'R00261 - Risk 6',
    type: SourceEntityTypeEnum.RISK,
  },
  {
    id: '66',
    label: 'R00616 - Risk 6',
    type: SourceEntityTypeEnum.RISK,
  },
  {
    id: '76',
    label: 'R007326 - Risk 6',
    type: SourceEntityTypeEnum.RISK,
  },
  {
    id: '86',
    label: 'R008416 - Risk 6',
    type: SourceEntityTypeEnum.RISK,
  },
];

const fetchMockObjects = (
  filter?: ObjectFilterDtoOptions,
  page = 0,
  limit = 10,
): Promise<PaginatedMockObject> =>
  new Promise((resolve) => {
    const items = mockData;

    const itemCount = items.length;
    const pageCount = Math.ceil(itemCount / limit);
    const start = page * limit;
    const end = start + limit;

    const filteredItems = items.filter(
      (item) =>
        (!filter?.type || item.type === filter.type) &&
        item.label.includes(filter?.searchTerm ?? ''),
    );

    const pagedItems = filteredItems.slice(start, end);

    const meta = {
      page,
      limit,
      itemCount,
      pageCount,
      hasPreviousPage: page > 0,
      hasNextPage: page < pageCount - 1,
    };

    resolve({
      data: pagedItems,
      meta,
    });
  });

export function useInfiniteObjects(params: UseInfiniteObjectParams = {}) {
  const {
    page = 0,
    limit = 10,
    sort,
    filter,
    enabled = true,
    handleError = false,
  } = params;

  const filters = {
    ...filter,
    searchTerm: encodeURIComponent(filter?.searchTerm ?? ''),
  };

  const queryKey = ['objects', page, limit, sort, filters, enabled];

  return useInfiniteQuery<
    PaginatedMockObject,
    ExtendedAxiosError,
    InfiniteData<PaginatedMockObject>,
    typeof queryKey,
    number
  >({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchMockObjects(filter, pageParam, limit).then((data) => data),
    enabled,
    initialPageParam: page,
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined,
    meta: {
      handleError,
    },
  });
}
